import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AgeingReport,
  CustomerReceivable,
  InstallmentWindow,
  InstallmentWorkItem,
  OrderReceivable,
  OutstandingCustomer,
  PaginatedResult,
  PaymentMethod,
  PaymentPlanDetail,
  PaymentPlanStatus,
  PaymentPlanSummary,
  SalesCreditNoteSummary,
} from '@optiflow/types';
import { api } from '@/services/apiClient';
import { useIdempotentMutation } from '@/services/useIdempotentMutation';
import { salesKeys } from './sales.api';

export interface PlanListParams {
  page?: number;
  pageSize?: number;
  status?: PaymentPlanStatus | '';
  overdueOnly?: boolean;
  search?: string;
}

export const receivableKeys = {
  all: [...salesKeys.all, 'receivables'] as const,
  customer: (id: string) => [...receivableKeys.all, 'customer', id] as const,
  order: (id: string) => [...receivableKeys.all, 'order', id] as const,
  plans: (params: PlanListParams) => [...receivableKeys.all, 'plans', params] as const,
  plan: (id: string) => [...receivableKeys.all, 'plan', id] as const,
  orderPlan: (orderId: string) => [...receivableKeys.all, 'order-plan', orderId] as const,
  outstanding: () => [...receivableKeys.all, 'outstanding'] as const,
  installments: (window: InstallmentWindow) =>
    [...receivableKeys.all, 'installments', window] as const,
  ageing: () => [...receivableKeys.all, 'ageing'] as const,
  creditNotes: (orderId: string) => [...receivableKeys.all, 'credit-notes', orderId] as const,
};

export function useCustomerReceivableQuery(customerId: string | undefined) {
  return useQuery({
    queryKey: receivableKeys.customer(customerId ?? ''),
    queryFn: () => api.get<CustomerReceivable>(`/sales/customers/${customerId ?? ''}/receivable`),
    enabled: Boolean(customerId),
  });
}

export function useOrderReceivableQuery(orderId: string | undefined) {
  return useQuery({
    queryKey: receivableKeys.order(orderId ?? ''),
    queryFn: () => api.get<OrderReceivable>(`/sales/orders/${orderId ?? ''}/receivable`),
    enabled: Boolean(orderId),
  });
}

export function useOrderPlanQuery(orderId: string | undefined) {
  return useQuery({
    queryKey: receivableKeys.orderPlan(orderId ?? ''),
    queryFn: () => api.get<PaymentPlanDetail | null>(`/sales/orders/${orderId ?? ''}/plan`),
    enabled: Boolean(orderId),
  });
}

export function usePaymentPlanQuery(planId: string | undefined) {
  return useQuery({
    queryKey: receivableKeys.plan(planId ?? ''),
    queryFn: () => api.get<PaymentPlanDetail>(`/sales/plans/${planId ?? ''}`),
    enabled: Boolean(planId),
  });
}

export function usePaymentPlansQuery(params: PlanListParams) {
  return useQuery({
    queryKey: receivableKeys.plans(params),
    queryFn: () =>
      api.get<PaginatedResult<PaymentPlanSummary>>('/sales/plans', {
        params: {
          page: params.page ?? 1,
          pageSize: params.pageSize ?? 20,
          ...(params.status ? { status: params.status } : {}),
          ...(params.overdueOnly ? { overdueOnly: true } : {}),
          ...(params.search?.trim() ? { search: params.search.trim() } : {}),
        },
      }),
    placeholderData: (previous) => previous,
  });
}

/** All three worklists are branch-scoped, so they wait for a branch rather than asking without one. */
export function useOutstandingCustomersQuery(enabled = true) {
  return useQuery({
    queryKey: receivableKeys.outstanding(),
    queryFn: () => api.get<OutstandingCustomer[]>('/sales/receivables/customers'),
    enabled,
  });
}

export function useInstallmentsQuery(window: InstallmentWindow, enabled = true) {
  return useQuery({
    queryKey: receivableKeys.installments(window),
    queryFn: () =>
      api.get<InstallmentWorkItem[]>('/sales/receivables/installments', { params: { window } }),
    enabled,
  });
}

export function useAgeingQuery(enabled = true) {
  return useQuery({
    queryKey: receivableKeys.ageing(),
    queryFn: () => api.get<AgeingReport>('/sales/receivables/ageing'),
    enabled,
  });
}

export function useCreditNotesQuery(orderId: string | undefined) {
  return useQuery({
    queryKey: receivableKeys.creditNotes(orderId ?? ''),
    queryFn: () =>
      api.get<PaginatedResult<SalesCreditNoteSummary>>('/sales/credit-notes', {
        params: { orderId: orderId ?? '', pageSize: 50 },
      }),
    enabled: Boolean(orderId),
  });
}

/** Money moving changes balances everywhere, so the whole sales cache goes. */
function useReceivableInvalidation() {
  const queryClient = useQueryClient();
  return () => void queryClient.invalidateQueries({ queryKey: salesKeys.all });
}

export interface SettlePayload {
  method: PaymentMethod;
  amountMinor: number;
  reference?: string;
  allocations?: { installmentId: string; amountMinor: number }[];
}

export function useSettlePlanMutation(planId: string) {
  const invalidate = useReceivableInvalidation();

  return useIdempotentMutation<PaymentPlanDetail, SettlePayload>(
    (payload, config) =>
      api.post<PaymentPlanDetail>(`/sales/plans/${planId}/settle`, payload, config),
    { onSuccess: invalidate },
  );
}

export function useCancelPlanMutation(planId: string) {
  const invalidate = useReceivableInvalidation();

  return useMutation({
    mutationFn: (reason: string) =>
      api.post<PaymentPlanDetail>(`/sales/plans/${planId}/cancel`, { reason }),
    onSuccess: invalidate,
  });
}

export function useSetCreditLimitMutation(customerId: string) {
  const invalidate = useReceivableInvalidation();

  return useMutation({
    mutationFn: (payload: { creditLimitMinor: number | null; reason: string }) =>
      api.post<CustomerReceivable>(`/sales/customers/${customerId}/credit-limit`, payload),
    onSuccess: invalidate,
  });
}
